import { useEffect, useMemo, useState } from 'react';
import {
  createEmailTemplate,
  deleteEmailTemplate,
  listEmailTemplates,
  previewEmailTemplate,
  updateEmailTemplate,
} from '../../../api/adminEmailCenter';
import EmailCenterPage, {
  EmptyState,
  PageSection,
  SelectField,
  StatusBadge,
  TextAreaField,
  TextField,
} from './EmailCenterShared';

const blankTemplate = {
  id: null,
  code: '',
  name: '',
  subject: '',
  htmlBody: '<div><h1>Hello {{name}}</h1><p>Template body</p></div>',
  textBody: '',
  category: 'custom',
  isActive: true,
};

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(blankTemplate);
  const [preview, setPreview] = useState({ subject: '', htmlBody: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await listEmailTemplates();
      setTemplates(data.items || []);
      if (!form.id && data.items?.length) {
        setForm(data.items[0]);
      }
    } catch (error) {
      setError(error.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await previewEmailTemplate({
          subject: form.subject,
          htmlBody: form.htmlBody,
          textBody: form.textBody,
          variables: {
            name: 'Preview User',
            first_name: 'Preview',
            email: 'preview@homevilas.com',
            campaign_name: 'Launch Offer',
            support_email: 'info@homevilas.com',
            unsubscribe_url: '#',
          },
        });
        if (!active) return;
        setPreview(data.preview || { subject: '', htmlBody: '' });
      } catch (_) {
        if (!active) return;
      }
    })();
    return () => {
      active = false;
    };
  }, [form.subject, form.htmlBody, form.textBody]);

  const templateCount = useMemo(() => templates.length, [templates]);

  const save = async () => {
    setSaving(true);
    setError('');
    setInfo('');
    try {
      const payload = {
        code: form.code,
        name: form.name,
        subject: form.subject,
        htmlBody: form.htmlBody,
        textBody: form.textBody,
        category: form.category,
        isActive: form.isActive,
      };
      if (form.id) {
        await updateEmailTemplate(form.id, payload);
        setInfo('Template updated.');
      } else {
        await createEmailTemplate(payload);
        setInfo('Template created.');
      }
      await load();
    } catch (error) {
      setError(error.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Archive this template?')) return;
    try {
      await deleteEmailTemplate(id);
      setForm(blankTemplate);
      await load();
    } catch (error) {
      setError(error.message || 'Failed to archive template');
    }
  };

  const duplicateCurrent = () => {
    setForm((current) => ({
      ...current,
      id: null,
      code: current.code ? `${current.code}_COPY` : '',
      name: current.name ? `${current.name} Copy` : '',
    }));
  };

  return (
    <EmailCenterPage
      title="Template Library"
      description="Reusable promotional, booking, support, and payment templates with variable preview and activation controls."
      actions={
        <button type="button" onClick={() => setForm(blankTemplate)} className="rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_32px_rgba(15,118,110,0.24)]">
          New Template
        </button>
      }
    >
      {error ? <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      {info ? <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{info}</div> : null}

      <div className="grid gap-6 xl:grid-cols-[0.8fr_1.2fr]">
        <PageSection title="Template Catalog" subtitle={`${templateCount} templates available across transactional, promotional, and custom categories.`}>
          {!templates.length && !loading ? (
            <EmptyState title="No templates yet" body="Create a reusable HTML template for promotions, invoices, support, or custom sends." />
          ) : (
            <div className="space-y-3">
              {templates.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setForm(item)}
                  className={`w-full rounded-[24px] border p-4 text-left transition ${form.id === item.id ? 'border-emerald-500 bg-emerald-50/60' : 'border-slate-200 bg-white'}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{item.name}</p>
                      <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">{item.code}</p>
                    </div>
                    <StatusBadge value={item.isActive ? 'active' : 'inactive'} />
                  </div>
                  <p className="mt-3 text-sm text-slate-500">{item.subject}</p>
                </button>
              ))}
            </div>
          )}
        </PageSection>

        <div className="space-y-6">
          <PageSection
            title={form.id ? 'Edit Template' : 'Create Template'}
            subtitle="Subject, HTML body, plain text fallback, and placeholder support live here."
            action={
              <div className="flex flex-wrap gap-2">
                {form.id ? (
                  <>
                    <button type="button" onClick={duplicateCurrent} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700">
                      Duplicate
                    </button>
                    <button type="button" onClick={() => remove(form.id)} className="rounded-full border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-700">
                      Archive
                    </button>
                  </>
                ) : null}
                <button type="button" onClick={save} disabled={saving} className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            }
          >
            <div className="grid gap-4 md:grid-cols-2">
              <TextField label="Code" value={form.code} onChange={(e) => setForm((current) => ({ ...current, code: e.target.value }))} />
              <TextField label="Name" value={form.name} onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))} />
              <TextField label="Subject" value={form.subject} onChange={(e) => setForm((current) => ({ ...current, subject: e.target.value }))} />
              <SelectField label="Category" value={form.category} onChange={(e) => setForm((current) => ({ ...current, category: e.target.value }))}>
                <option value="custom">Custom</option>
                <option value="promotional">Promotional</option>
                <option value="booking">Booking</option>
                <option value="invoice">Invoice</option>
                <option value="support">Support</option>
                <option value="welcome">Welcome</option>
                <option value="payment">Payment</option>
                <option value="transactional">Transactional</option>
                <option value="system">System</option>
              </SelectField>
              <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((current) => ({ ...current, isActive: e.target.checked }))} />
                Active
              </label>
              <div />
              <div className="md:col-span-2">
                <TextAreaField label="HTML Body" value={form.htmlBody} onChange={(e) => setForm((current) => ({ ...current, htmlBody: e.target.value }))} className="min-h-[280px] font-mono" />
              </div>
              <div className="md:col-span-2">
                <TextAreaField label="Text Body" value={form.textBody} onChange={(e) => setForm((current) => ({ ...current, textBody: e.target.value }))} className="min-h-[160px] font-mono" />
              </div>
            </div>
          </PageSection>

          <PageSection title="Rendered Preview" subtitle="Preview variables: {{name}}, {{first_name}}, {{email}}, {{campaign_name}}, {{support_email}}, {{unsubscribe_url}}">
            <div className="rounded-[24px] border border-slate-100 bg-slate-50/50 p-4">
              <p className="text-sm font-semibold text-slate-900">{preview.subject || 'No subject'}</p>
              <div className="prose mt-4 max-w-none text-sm" dangerouslySetInnerHTML={{ __html: preview.htmlBody || '<p>No body</p>' }} />
            </div>
          </PageSection>
        </div>
      </div>
    </EmailCenterPage>
  );
}
